import { css } from '@emotion/react';
import { Descriptions, Skeleton, Table, Tabs, Tag } from 'antd';

import BaseModal from '~/common/components/modals/BaseModal';
import { useAppDispatch, useAppSelector } from '~/common/stores';
import { PaymentType, PurchaseStatus } from '~/features/purchase/types/purchase.type';
import { resetUserDetailModal } from '~/features/user/stores/user-detail-modal.slice';

import type { ColumnsType } from 'antd/es/table';

import type { Purchase } from '~/features/purchase/types/purchase.type';

function UserDetailModal() {
  const dispatch = useAppDispatch();
  const { user, purchases, isLoading } = useAppSelector(state => state.userDetailModal);

  const handleClose = () => {
    dispatch(resetUserDetailModal());
  };

  const getStatusTag = (status: PurchaseStatus) => {
    const config: Record<PurchaseStatus, { color: string; label: string }> = {
      [PurchaseStatus.PENDING]: { color: 'processing', label: '대기' },
      [PurchaseStatus.CASH_PENDING]: { color: 'warning', label: '입금대기' },
      [PurchaseStatus.COMPLETED]: { color: 'success', label: '완료' },
      [PurchaseStatus.FAILED]: { color: 'error', label: '실패' },
      [PurchaseStatus.CANCELLED]: { color: 'default', label: '취소' },
      [PurchaseStatus.REFUNDED]: { color: 'magenta', label: '환불' },
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Tag color={color}>{label}</Tag>;
  };

  const purchaseColumns: ColumnsType<Purchase> = [
    {
      title: '상품',
      dataIndex: ['product', 'name'],
      key: 'productName',
    },
    {
      title: '금액',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount.toLocaleString()}원`,
    },
    {
      title: '결제방식',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (type: PaymentType) => (type === PaymentType.CASH ? '현금' : '토스페이'),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: PurchaseStatus) => getStatusTag(status),
    },
    {
      title: '구매일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ko-KR'),
    },
  ];

  const tabItems = [
    {
      key: 'info',
      label: '기본정보',
      children: isLoading ? (
        <Skeleton paragraph={{ rows: 4 }} active />
      ) : user ? (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="사용자 계정">{user.userId}</Descriptions.Item>
          <Descriptions.Item label="이름">{user.name}</Descriptions.Item>
          <Descriptions.Item label="전화번호">{user.phoneNumber}</Descriptions.Item>
          <Descriptions.Item label="SMS 수신">
            <Tag color={user.smsConsent ? 'green' : 'default'}>
              {user.smsConsent ? '동의' : '거부'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="추천코드">{user.referralCode || '-'}</Descriptions.Item>
          <Descriptions.Item label="가입일">
            {new Date(user.createdAt).toLocaleDateString('ko-KR')}
          </Descriptions.Item>
        </Descriptions>
      ) : null,
    },
    {
      key: 'purchases',
      label: `결제내역 (${purchases.length})`,
      children: (
        <Table
          columns={purchaseColumns}
          dataSource={purchases}
          loading={isLoading}
          pagination={false}
          rowKey="id"
          scroll={{ y: 300 }}
          size="small"
        />
      ),
    },
  ];

  return (
    <BaseModal
      title={user ? `${user.name} 상세정보` : '사용자 상세정보'}
      name="UserDetailModal"
      footer={null}
      width={600}
      closable
      maskClosable
      onCancel={handleClose}
    >
      <div css={styles.content}>
        <Tabs defaultActiveKey="info" items={tabItems} />
      </div>
    </BaseModal>
  );
}

const styles = {
  content: css`
    min-height: 300px;
  `,
};

export default UserDetailModal;
